import getLogger from '@ignitesales/logger-es';
import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import deepmerge from 'deepmerge';
import { AppActions } from './AppActions';
import NeuroRoot from '../../NeuroRoot';
import ignite from '../../services';
import { IntlProvider } from 'react-intl';
import { ErrorBoundary, MessageProvider, QuestionContextProvider } from '../../components';
import { createStrictContext, initializeReducer, initializeState } from '../_utils';
import { isFunction } from '../../utils';
import {loadProductCart} from '../Product/ProductActions';

const logger = getLogger({ module: 'AppContext' });
const [AppContext, useAppContext] = createStrictContext({
  name: 'NeuroAppContext',
});

const AppProvider = ({
  AppComponent,
  AppLoadingComponent,
  customMessages,
  htmlMessageValues,
  locale,
  messages,
  questionGroupTypes,
  questionTypes,
  reducers,
  timeout = 2000
}) => {
  const initialLocalState = {
    guideConfigurations: {
      runMode: ignite.getRunMode(),
      isMockMode: ignite?.isUsingMockService || false,
    }
  };

  const appReducers = React.useMemo(() => {
    return initializeReducer({ reducers });
  }, [reducers]);

  const [state, dispatch] = useReducer(
    appReducers,
    initialLocalState
  );

  useEffect(() => {
    const fetchAppState = async () => {
      const loadInitialState = ignite.loadInitialState;
      const initialAppState = await initializeState({ loadInitialState });
      const initialState = deepmerge( initialLocalState, initialAppState );
      dispatch({
        type: AppActions.APP_READY,
        payload: {
          ...initialState,
          isAppReady: true,
          dispatch: appDispatch,
        },
      });
    };
    appDispatch(loadProductCart());

    //imitate loading screen in mock mode
    setTimeout(fetchAppState, window?.ignite?.isUsingMockService ? timeout : 0);
  }, []);

  //allows action creators to be used in familiar redux pattern
  const appDispatch = (param) => {
    try {
      if (isFunction(param)) {
        param(appDispatch, () => (Object.assign({}, state)));
      } else {
        dispatch(param);
      }
    } catch (ex) {
      logger.error('Unable to process dispatch call', ex);
    }
  };

  const contextValue = React.useMemo(() => {
    return { ...(Object.assign({}, state)), dispatch: appDispatch };
  }, [state, dispatch]);

  return (
    <AppContext.Provider value={contextValue}>
      <IntlProvider locale={locale} messages={messages[locale]} onError={ (e) => logger.warn(e) } wrapRichTextChunksInFragment>
        <MessageProvider customMessages={customMessages} htmlMessageValues={htmlMessageValues}>
          <QuestionContextProvider questionGroupTypes={questionGroupTypes} questionTypes={questionTypes}>
            <ErrorBoundary>
              <NeuroRoot
                isLoading={!state?.isAppReady}
                LoadingComponent={AppLoadingComponent}
                AppComponent={AppComponent}
              />
            </ErrorBoundary>
          </QuestionContextProvider>
        </MessageProvider>
      </IntlProvider>
    </AppContext.Provider>
  );
};

/**
 * @return {NeuroStateInitObject} useNeuroState -  Neuro app state object
 */
const useNeuroState = () => {
  return useAppContext();
};

/**
 * @returns {Function} Dispatch function only from app state
 */
const useDispatch = () => {
  return useAppContext().dispatch;
};

AppProvider.propTypes = {
  reducers: PropTypes.any,
  AppComponent: PropTypes.any,
  AppLoadingComponent: PropTypes.any,
  customMessages: PropTypes.any,
  htmlMessageValues: PropTypes.object,
  appReducer: PropTypes.any,
  locale: PropTypes.string,
  messages: PropTypes.any,
  questionGroupTypes: PropTypes.any,
  questionTypes: PropTypes.any,
  shouldRenderLogin: PropTypes.bool,
  children: PropTypes.any,
  reducer: PropTypes.any,
  timeout: PropTypes.number
};

export { AppContext, AppProvider, useNeuroState, useDispatch };
export default useNeuroState;
